import type * as Geo from "@/base/geo"
import type {
  InstallationType,
  SensorName,
} from "@/base/sensor-configurations"
import type {
  MeasurementPreference,
  MeasurementPreferenceProps,
} from "@/base/sensor-conversions"
import type {
  SensorEvent,
  SensorEventKey,
  SensorState,
} from "@/base/sensor-events"
import type { VfdIndicatorProps } from "@/components/VfdIndicator"
import type {
  BadgeProps,
  ColorSchemeType,
  IconProp,
  NoChildren,
  TestId,
} from "@/theme"
import type { Reducer } from "@reduxjs/toolkit"
import type { Dictionary } from "lodash"
import type { SetDifference } from "utility-types"

import {
  formatDurationMs,
  formatHoursPerRevolution,
  getPivotActivityMetrics,
  getTimeToNextStopMs,
} from "@/base/device-activity-utils"
import { formatPivotHeading } from "@/base/pivot-formatters"
import {
  addDeviceAsync,
  loadDeviceActivityAsync,
  loadLastEventForDeviceAsync,
  removeDeviceFromFarmAsync,
  replaceDeviceAsync,
} from "@/base/farmhq-api"
import { getSensorUnitLabel } from "@/base/getSensorUnitLabel"
import { loadActiveFarmAsync } from "@/base/load-app"
import { isDeviceInMotion } from "@/base/sensor-configurations"
import { formatSensorValue } from "@/base/sensor-formatting"
import {
  sortByDeviceInstallationType,
  sortByDeviceName,
  sortDeviceStatusByUrgency,
  sortDeviceStatusByUrgencyAscending,
} from "@/base/sort-device-status"
import { makePrioritySort } from "@/base/sort-priority"
import { isNumberValid, isTruthyString } from "@/base/type-guards"
import { CENTER_PIVOT_INSTALLATION_TYPES } from "@/constants/base"
import { IMAGE_HOST_URL } from "@/constants/imgix"
import * as Models from "@/redux/models"
import i18n from "@/translations/i18n"
import { createSelector, createSlice, isAnyOf } from "@reduxjs/toolkit"
import { createCachedSelector } from "re-reselect"

import type {
  DeviceConfiguration,
  DeviceSummary,
} from "./device-configurations.reducer"
import type {
  ReelRunStatus,
  RunCompletion,
  RunEtaParams,
} from "./reel-runs.reducer"
import type { RootState } from "./root.reducer"

import type { SortComparator } from "@/base/types"
import { signOutAsync } from "./auth.reducer"
import {
  calculateReelRunEta,
  calculateReelRunStatus,
  calculateRunCompletion,
} from "./reel-runs.reducer"
import {
  findFieldForDevice,
  getActiveFarmCoordinatesFromState,
  getDeviceSortDirectionFromState,
  getDeviceSortKeyFromState,
  getIsAdminModeEnabledFromState,
  getUserMeasurementPreferenceFromState,
  makeDeviceEventCacheKey,
  makeDeviceSummary,
  makeLocationValidator,
} from "./selectors"

export interface StatusBadgeProps extends NoChildren<BadgeProps> {
  IconComponent: IconProp
  id: TestId
  text: string | undefined
}

export type FlowVolumeL24H = {[key: string]: number}

interface PendingSensorAction {
  sensorEventId: number
  timestamp: number
}

const PENDING_SENSOR_ACTION_EXPIRY_MS = 10_000

type DeviceEventLastState = Models.ModelState<Models.DeviceEvent, string> & {
  flowVolumeL24H?: FlowVolumeL24H
  sensorEventLasts: Record<string, Models.SensorEventLastEntry[]>
  pendingSensorActions: Record<number, PendingSensorAction>
}

const adapter = Models.deviceEventLast.adapter
const initialState: DeviceEventLastState = {
  ...Models.getInitialEntityState<Models.DeviceEvent, string>(),
  sensorEventLasts: {},
  pendingSensorActions: {},
}

const slice = createSlice({
  extraReducers: (builder) =>
    builder
      .addCase(loadLastEventForDeviceAsync.fulfilled, (state, { payload }) => {
        if (payload.event) {
          adapter.upsertOne(state, payload.event)
        }
      })
      .addCase(removeDeviceFromFarmAsync.fulfilled, (state, { meta }) => {
        adapter.removeOne(state, meta.arg.deviceId)
      })
      .addCase(addDeviceAsync.fulfilled, (state, { payload }) => {
        if (payload.status === "VALID" && payload.deviceEvent) {
          adapter.upsertOne(state, payload.deviceEvent)
        }
      })
      .addCase(replaceDeviceAsync.fulfilled, (state, { meta, payload }) => {
        if (payload.status === "VALID") {
          if (payload.deviceEvent) {
            adapter.upsertOne(state, payload.deviceEvent)
          }
          adapter.removeOne(state, meta.arg.deviceIdOld)
        }
      })
      .addCase(loadActiveFarmAsync.fulfilled, (state, action) => {
        state.flowVolumeL24H = action.payload.flowVolumeL24H?.reduce((acc, value) => {
          return {...acc, [value.deviceId]: value.flowVolumeLLast24H}
        }, {} as FlowVolumeL24H)
      })
      .addMatcher(
        isAnyOf(
          loadDeviceActivityAsync.fulfilled,
        ),
        (state, { payload }) => {
          // The device is 'primary keyed' on device id so upsert
          // should be sufficient to keep events fresh
          const events = payload.deviceEvents
          adapter.setAll(state, events)

          // Group sensor event lasts by deviceId
          const grouped: Record<string, Models.SensorEventLastEntry[]> = {}
          for (const entry of payload.sensorEventLasts ?? []) {
            const key = entry.deviceId
            if (!grouped[key]) {
              grouped[key] = []
            }
            grouped[key]!.push(entry)
          }
          state.sensorEventLasts = grouped

          // Auto-clear pending sensors: new event detected or expired
          const now = Date.now()
          for (const sensorIdStr of Object.keys(state.pendingSensorActions)) {
            const sensorId = Number(sensorIdStr)
            const pending = state.pendingSensorActions[sensorId]
            if (!pending) continue

            const isExpired =
              now - pending.timestamp >= PENDING_SENSOR_ACTION_EXPIRY_MS

            const matchingEntry = (payload.sensorEventLasts ?? []).find(
              (e) => e.sensorId === sensorId,
            )
            const hasNewEvent =
              matchingEntry !== undefined &&
              matchingEntry.sensorEventId !== pending.sensorEventId

            if (isExpired || hasNewEvent) {
              delete state.pendingSensorActions[sensorId]
            }
          }
        },
      )
      .addMatcher(isAnyOf(signOutAsync.fulfilled), () => {
        return { ...initialState, sensorEventLasts: {}, pendingSensorActions: {} }
      }),
  initialState,
  name: `deviceEventLast`,
  reducers: {
    markSensorActionPending: (
      state,
      action: { payload: { sensorId: number; sensorEventId: number } },
    ) => {
      state.pendingSensorActions[action.payload.sensorId] = {
        sensorEventId: action.payload.sensorEventId,
        timestamp: Date.now(),
      }
    },
  },
})
export const { markSensorActionPending } = slice.actions
const deviceEventLast: Reducer<DeviceEventLastState> = slice.reducer
export default deviceEventLast

export const isSensorActionPending = (
  state: RootState,
  sensorId: number,
): boolean => {
  const pending = state.deviceEventLast.pendingSensorActions[sensorId]
  if (!pending) return false
  return Date.now() - pending.timestamp < PENDING_SENSOR_ACTION_EXPIRY_MS
}

const EMPTY_SENSOR_EVENT_LASTS: Models.SensorEventLastEntry[] = []

export const getSensorEventLastsForDevice = (
  state: RootState,
  deviceId: string,
): Models.SensorEventLastEntry[] => {
  return state.deviceEventLast.sensorEventLasts[deviceId] ?? EMPTY_SENSOR_EVENT_LASTS
}

/**
 * If the run is in progress, show its ETA.
 */
function getRunEtaIndicatorProps({
  runDistanceMmCurrent,
  runSpeedMmpm,
  runStatus,
}: RunEtaParams & {
  runStatus: ReelRunStatus | null | undefined
}): StatusBadgeProps | undefined {
  const eta = calculateReelRunEta({ runDistanceMmCurrent, runSpeedMmpm })
  const id: TestId = "runEtaIndicator"
  const colorScheme: BadgeProps["colorScheme"] = undefined

  if (runStatus === "stopped short") {
    return {
      IconComponent: "Warning",
      colorScheme: "$orange",
      id,
      text: i18n.t("runStoppedShort", { ns: "devices" }),
    }
  }
  if (runStatus === "complete") {
    return {
      IconComponent: "Success",
      colorScheme: "$green",
      id,
      text: i18n.t("runComplete", { ns: "devices" }),
    }
  }
  if (runStatus === "retracting" && isNumberValid(eta)) {
    const options = {
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      month: "numeric",
    } as const

    return {
      IconComponent: "Clock",
      colorScheme,
      id,
      text: i18n.t("runEtaWithTimestamp", {
        ...options,
        ns: "devices",
        val: new Date(eta),
      }),
    }
  }
  return undefined
}

/**
 * If the reel is moving, show its state and speed
 */
function getRunSpeedBadgeProps({
  measurementPreference,
  runSpeedMmpm,
  runStatus,
}: MeasurementPreferenceProps & {
  runSpeedMmpm: number | null | undefined
  runStatus: ReelRunStatus
}) {
  const colorScheme: BadgeProps["colorScheme"] = "$info"

  if (runStatus === "stopped short") {
    return undefined
  }
  if (runStatus === "complete") {
    return undefined
  }

  const fieldName = "runSpeedMmpm"
  const base: Omit<StatusBadgeProps, "text"> = {
    IconComponent: "Speed",
    id: "runSpeedIndicator",
  }

  if (runStatus === "stopped") {
    return {
      ...base,
      text: i18n.t("runStopped", { ns: "devices" }),
    }
  }
  let sensorState: string
  if (runStatus === "extending") {
    sensorState = i18n.t(`sensorStates:RE.short`)
  } else {
    sensorState = i18n.t(`sensorStates:RR.short`)
  }
  if (isNumberValid(runSpeedMmpm)) {
    return {
      ...base,
      colorScheme,
      text: i18n.t("sensorStateWithSensorReadingAndUnitLabel", {
        ns: "devices",
        sensorReading: formatSensorValue({
          fieldName,
          measurementPreference,
          rawValue: runSpeedMmpm,
        }),
        sensorState,
        unitLabel: getSensorUnitLabel({
          fieldName,
          measurementPreference,
        }),
      }),
    }
  }
  return undefined
}

/**
 * Show a warning badge ONLY if the device has bad gps
 */
function getGpsBadgeProps({
  isLocationValid,
}: {
  isLocationValid: boolean
}): StatusBadgeProps | undefined {
  const id: TestId = "gpsIndicator"
  if (isLocationValid) {
    return undefined
  }
  const text = i18n.t("noGps", { ns: "devices" })
  return {
    IconComponent: "GpsOff",
    colorScheme: "$warning",
    id,
    text,
  }
}

/**
 * Badge for relay sensor (on/off)
 */
function getRelayBadgeProps({
  deviceInstallationType,
  stateCurrent,
}: {
  deviceInstallationType: InstallationType
  stateCurrent: SensorState<"relay"> | null | undefined
}): StatusBadgeProps | undefined {
  const id: TestId = "relayIndicator"
  if (
    deviceInstallationType === "valve" ||
    deviceInstallationType === "pump" ||
    deviceInstallationType === "pump_on_off" ||
    deviceInstallationType === "pump_vfd" ||
    deviceInstallationType === "reel_with_booster_off_only"
  ) {
    switch (stateCurrent) {
      case null:
      case undefined:
      case "NONE": {
        break
      }
      case "OFF": {
        return {
          IconComponent: "SwitchOpen",
          id,
          text: i18n.t("sensorStates:OFF.short"),
        }
      }
      case "ON": {
        return {
          IconComponent: "SwitchClosed",
          colorScheme: "$success",
          id,
          text: i18n.t("sensorStates:ON.short"),
        }
      }
    }
  }
  return undefined
}

/**
 * Badge for hall switch
 */
function getHallSwitchProps({
  deviceInstallationType,
  stateCurrent,
}: {
  deviceInstallationType: InstallationType
  stateCurrent: SensorState<"hallSwitch"> | null | undefined
}): StatusBadgeProps | undefined {
  const id: TestId = "hallSwitchIndicator"

  if (
    deviceInstallationType === "pump" ||
    deviceInstallationType === "prototype"
  ) {
    switch (stateCurrent) {
      case null:
      case undefined:
      case "NONE": {
        break
      }
      case "MA": {
        return {
          IconComponent: "Magnet",
          id,
          text: i18n.t("sensorStates:MA.short"),
        }
      }
      case "MP": {
        return {
          IconComponent: "MagnetOn",
          colorScheme: "$success",
          id,
          text: i18n.t("sensorStates:MP.short"),
        }
      }
    }
  }
  return undefined
}


/**
 * Show a flow badge if possible, otherwise no flow.
 */
function getFlowBadgeProps({
  flowRateLpmAvg,
  flowStateCurrent,
  flowVolumeL24H,
  measurementPreference,
}: {
  flowRateLpmAvg: number | null | undefined
  flowStateCurrent: SensorState<"flow"> | null | undefined
  flowVolumeL24H: number | null | undefined
  measurementPreference: MeasurementPreference
}): StatusBadgeProps {
  const id: TestId = "flowIndicator"
  if (!flowStateCurrent || flowStateCurrent === "NONE") {
    flowStateCurrent = "FLN"
  }

  // Format the text
  const sensorState = i18n.t(`sensorStates:${flowStateCurrent}.short`)

  let text = sensorState
  if (isNumberValid(flowRateLpmAvg)) {
    const sensorReading = formatSensorValue({
      fieldName: "rateLpmAvg",
      measurementPreference,
      rawValue: flowRateLpmAvg,
    })
    

    let volumeText = ''
    if ( isNumberValid(flowVolumeL24H) ) {
      const flowVolume24HTotal = formatSensorValue({
        fieldName: "volumeLTotal",
        measurementPreference,
        rawValue: flowVolumeL24H
      })
      if (typeof flowVolume24HTotal === "string") {
        const unitLabel = getSensorUnitLabel({
          fieldName: "volumeLTotal",
          measurementPreference,
        })
        volumeText = ` (${flowVolume24HTotal} ${unitLabel} last 24h)`
      }
    }

    if (typeof sensorReading === "string") {
      const unitLabel = getSensorUnitLabel({
        fieldName: "flowRateEstimatedLpm",
        measurementPreference,
      })
      text = `${sensorReading} ${unitLabel}${volumeText}`
    }
  }

  // Get badge style
  let IconComponent: IconProp
  let colorScheme: ColorSchemeType
  if (flowStateCurrent === "FLF") {
    IconComponent = "FlowOn"
    colorScheme = "$blue"
  } else if(flowStateCurrent === "FLOV") {
    IconComponent = "FlowOn"
    colorScheme = "$orange"
  } else {
    IconComponent = "FlowOff"
    colorScheme = "$gray"
  }

  return { IconComponent, colorScheme, id, text }
}

/**
 * Badge for battery external voltage
 */
function getVoltageBadgeProps({
  internalSoc,
  measurementPreference,
  stateCurrent,
  voltageMv,
}: MeasurementPreferenceProps &
  Pick<SensorEvent<"battery">, "internalSoc" | "stateCurrent" | "voltageMv">):
  | StatusBadgeProps
  | undefined {
  let colorScheme: BadgeProps["colorScheme"] = "$gray"

  switch (stateCurrent) {
    case undefined:
    case "NONE":
    case null: {
      break
    }
    case "BES":
    case "BD":
    case "BL": {
      colorScheme = "$warning"
      break
    }
    case "BN": {
      colorScheme = "$success"
      break
    }
  }

  if (isNumberValid(voltageMv)) {
    let voltageFormatted = formatSensorValue({
      fieldName: "voltageMv",
      measurementPreference,
      rawValue: voltageMv,
    })

    if (isTruthyString(voltageFormatted)) {
      voltageFormatted = `${voltageFormatted}V`
      if (typeof internalSoc === "number") {
        voltageFormatted = `${voltageFormatted} (${internalSoc}%)`
      }
      return {
        IconComponent: "VoltageBolt",
        colorScheme,
        id: "voltageIndicator",
        text: voltageFormatted,
      }
    }
  }
  return undefined
}

/**
 * Badge for temperature
 */
function getTemperatureBadgeProps({
  measurementPreference,
  stateCurrent,
  readingCelsius,
}: MeasurementPreferenceProps &
  Pick<SensorEvent<"temperature">, "readingCelsius" | "stateCurrent">):
  | StatusBadgeProps
  | undefined {
  let colorScheme: BadgeProps["colorScheme"] = "$gray"

  switch (stateCurrent) {
    case undefined:
    case "NONE":
    case null: {
      break
    }
    case "THI": {
      colorScheme = "$warning"
      break
    }
    case "TLO": {
      colorScheme = "$warning"
      break
    }
    case "TNO": {
      colorScheme = "$success"
      break
    }
  }

  if (isNumberValid(readingCelsius)) {
    let temperatureFormatted = formatSensorValue({
      convertValues: true,
      fieldName: "readingCelsius",
      measurementPreference,
      rawValue: readingCelsius,
    })
    let unitLabel = getSensorUnitLabel({
        fieldName: "readingCelsius",
        measurementPreference,
    })

    return {
      IconComponent: "Temperature",
      colorScheme,
      id: "temperatureIndicator",
      text: `${temperatureFormatted} ${unitLabel}`,
    }
  }
  return undefined
}

/**
 * Badge for humidity
 */
function getHumidityBadgeProps({
  stateCurrent,
  relativePercentage,
}: MeasurementPreferenceProps &
  Pick<SensorEvent<"humidity">, "relativePercentage" | "stateCurrent">):
  | StatusBadgeProps
  | undefined {
  let colorScheme: BadgeProps["colorScheme"] = "$gray"

  switch (stateCurrent) {
    case undefined:
    case "NONE":
    case null: {
      break
    }
    case "HHI": {
      colorScheme = "$warning"
      break
    }
    case "HLO": {
      colorScheme = "$warning"
      break
    }
    case "HNO": {
      colorScheme = "$success"
      break
    }
  }

  if (isNumberValid(relativePercentage)) {
    return {
      IconComponent: "Humidity",
      colorScheme,
      id: "humidityIndicator",
      text: `${Math.trunc(relativePercentage)}%`,
    }
  }
  return undefined
}

/**
 * Badge for depth
 */
function getDepthBadgeProps({
  measurementPreference,
  stateCurrent,
  measuredM,
}: MeasurementPreferenceProps &
  Pick<SensorEvent<"depth">, "measuredM" | "stateCurrent">):
  | StatusBadgeProps
  | undefined {
  const sensorState = stateCurrent ?? "NONE"

  let iconName: IconProp | undefined
  let colorScheme: BadgeProps["colorScheme"] = "$gray"
  let text = i18n.t(`sensorStates:${sensorState}.short`)
  switch (stateCurrent) {
    case "DPO": {
      iconName = "SensorDepth"
      colorScheme = "$red"
      break
    }
    case "DPH": {
      iconName = "SensorDepth"
      colorScheme = "$warning"
      break
    }
    case "DPN": {
      iconName = "SensorDepth"
      colorScheme = "$success"
      break
    }
    case "DPL": {
      iconName = "SensorDepth"
      colorScheme = "$gray"
      break
    }
    case "NONE":
    case undefined:
    case null: {
      iconName = "SensorDepth"
      colorScheme = "$gray"
      break
    }
  }
  if (isNumberValid(measuredM)) {
    const sensorReading = formatSensorValue({
      fieldName: "measuredM",
      measurementPreference,
      rawValue: measuredM,
    })
    if (isTruthyString(sensorReading)) {
      text = i18n.t(`sensorStateWithSensorReadingAndUnitLabel`, {
        ns: "devices",
        sensorReading,
        sensorState: i18n.t(`sensorStates:${sensorState}.short`),
        unitLabel: getSensorUnitLabel({
          fieldName: "measuredM",
          measurementPreference,
        }),
      })
    }

    return {
      IconComponent: iconName,
      colorScheme,
      id: "depthIndicator",
      text,
    }
  }
  return undefined
}

/**
 * Badge for pressure sensor
 */
function getPressureBadgeProps({
  measurementPreference,
  pressureReadingPsi,
  pressureState,
}: MeasurementPreferenceProps & {
  pressureReadingPsi: number | null | undefined
  pressureState: SensorState<"pressure"> | null | undefined
}): StatusBadgeProps | undefined {
  const sensorState = pressureState ?? "NONE"

  let iconName: IconProp | undefined
  let colorScheme: BadgeProps["colorScheme"] = "$gray"
  let text = i18n.t(`sensorStates:${sensorState}.short`)
  switch (pressureState) {
    case "POV": {
      iconName = "GaugeFull"
      colorScheme = "$warning"
      break
    }
    case "PHI": {
      iconName = "Gauge"
      colorScheme = "$blue"
      break
    }
    case "PLO":
    case "NONE":
    case undefined:
    case null: {
      iconName = "GaugeEmpty"
      colorScheme = "$gray"
      break
    }
  }
  if (isNumberValid(pressureReadingPsi)) {
    const sensorReading = formatSensorValue({
      fieldName: "readingKpa",
      measurementPreference,
      rawValue: pressureReadingPsi,
    })
    if (isTruthyString(sensorReading)) {
      text = i18n.t(`sensorStateWithSensorReadingAndUnitLabel`, {
        ns: "devices",
        sensorReading,
        sensorState: i18n.t(`sensorStates:${sensorState}.short`),
        unitLabel: getSensorUnitLabel({
          fieldName: "readingKpa",
          measurementPreference,
        }),
      })
    }

    return {
      IconComponent: iconName,
      colorScheme,
      id: "pressureIndicator",
      text,
    }
  }
  return undefined
}

function getWheelBadgeProps({
  event,
  measurementPreference,
}: MeasurementPreferenceProps & {
  event:
    | Pick<SensorEvent<"wheel">, "speedMmpm" | "stateCurrent">
    | null
    | undefined
}): StatusBadgeProps {
  const reportedSpeedMmpm = event?.speedMmpm ?? 0

  let colorScheme: ColorSchemeType
  let reportedState: string

  switch (event?.stateCurrent) {
    case undefined:
    case null:
    case "WUNK":
    case "NONE":
    case "WS": {
      reportedState = i18n.t("sensorStates:WS.short")
      colorScheme = "$gray"
      break
    }
    case "WF": {
      reportedState = i18n.t("sensorStates:WF.short")
      colorScheme = "$blueGray"
      break
    }
    case "WL": {
      reportedState = i18n.t("sensorStates:WL.short")
      colorScheme = "$blue"
      break
    }
  }
  const fieldName: SensorEventKey = "speedMmpm"
  return {
    IconComponent: "Wheel",
    colorScheme,
    id: "wheel",
    text: i18n.t("sensorStateWithSensorReadingAndUnitLabel", {
      ns: "devices",
      sensorReading: formatSensorValue({
        fieldName,
        measurementPreference,
        rawValue: reportedSpeedMmpm,
      }),
      sensorState: reportedState,
      unitLabel: getSensorUnitLabel({
        fieldName,
        measurementPreference,
      }),
    }),
  }
}

/**
 * Four control-state badges — Run, Direction, Wet, End Gun — derived from the
 * controller's CP SensorEventLast `centerPivot*` integer fields. Mirrors the
 * logic in parseConfirmedState in CenterPivotControlContext so badges stay
 * consistent with the pivot control panel.
 */
function getPivotControlBadges(
  cpEntry: Models.SensorEventLastEntry | undefined,
): Pick<
  SensorStatusBadges,
  "pivotRun" | "pivotDirection" | "pivotWet" | "pivotEndGun"
> {
  if (!cpEntry) return {}

  const isRunning = cpEntry.centerPivotRun === 1
  // centerPivotDir: 0 = stopped, 1 = forward, 2 = reverse. null = unknown.
  const isForward = cpEntry.centerPivotDir === 1
  const isReverse = cpEntry.centerPivotDir === 2
  const isWet = cpEntry.centerPivotWet === 1
  const isEndGunOn = cpEntry.centerPivotEndGun === 1

  const active: ColorSchemeType = "$success"
  const dim: ColorSchemeType = "$gray"

  return {
    pivotRun: {
      IconComponent: "Wheel",
      colorScheme: isRunning ? active : dim,
      id: "pivotRunIndicator",
      text: isRunning ? "Running" : "Not Running",
    },
    pivotDirection: {
      IconComponent: isForward
        ? "ArrowUp"
        : isReverse
          ? "ArrowDown"
          : "Target",
      colorScheme: isForward || isReverse ? active : dim,
      id: "pivotDirectionIndicator",
      text: isForward ? "Fwd" : isReverse ? "Rev" : "In Place",
    },
    pivotWet: {
      IconComponent: "Water",
      colorScheme: isWet ? active : dim,
      id: "pivotWetIndicator",
      text: isWet ? "Wet" : "Dry",
    },
    pivotEndGun: {
      IconComponent: "Spray",
      colorScheme: isEndGunOn ? active : dim,
      id: "pivotEndGunIndicator",
      text: "Gun",
    },
  }
}

/**
 * Badge for center pivot arm heading. Prefers the activity-derived heading
 * (live during a run); falls back to the sticky last-known GPS-fitted heading
 * so idle pivots still show a meaningful angle.
 */
function getPivotHeadingBadgeProps({
  activity,
  lastKnownPivotHeadingDegrees,
}: {
  activity: Models.DeviceActivity | undefined
  lastKnownPivotHeadingDegrees: number | null | undefined
}): StatusBadgeProps | undefined {
  const activityHeading = activity
    ? getPivotActivityMetrics(activity).currentHeadingDegrees
    : null
  const heading = activityHeading ?? lastKnownPivotHeadingDegrees ?? null
  const text = formatPivotHeading(heading)
  if (!text) return undefined
  return {
    IconComponent: "Compass",
    colorScheme: "$gray",
    id: "pivotHeadingIndicator",
    text,
  }
}

/**
 * Badge for center pivot speed (hours per revolution)
 */
function getPivotSpeedBadgeProps({
  activity,
}: {
  activity: Models.DeviceActivity | undefined
}): StatusBadgeProps | undefined {
  if (!activity) return undefined

  const metrics = getPivotActivityMetrics(activity)

  if (metrics.hoursPerRevolution === null) {
    return undefined
  }

  const speedFormatted = formatHoursPerRevolution(metrics.hoursPerRevolution)
  if (!speedFormatted) return undefined

  // Choose icon based on direction
  let icon: IconProp = "Speed"
  if (metrics.direction === "pivot_clockwise") {
    icon = "RotateClockwise"
  } else if (metrics.direction === "pivot_counterclockwise") {
    icon = "RotateCounterClockwise"
  }

  const colorScheme: ColorSchemeType = metrics.direction ? "$blue" : "$gray"

  return {
    IconComponent: icon,
    colorScheme,
    id: "pivotSpeedIndicator",
    text: `${speedFormatted}/rev`,
  }
}

/**
 * Badge for center pivot time to next stop
 */
function getPivotTimeToStopBadgeProps({
  activity,
}: {
  activity: Models.DeviceActivity | undefined
}): StatusBadgeProps | undefined {
  if (!activity) return undefined

  const metrics = getPivotActivityMetrics(activity)

  const timeToStopMs = getTimeToNextStopMs(metrics.nextStopExpectedTimestamp)
  const timeFormatted = formatDurationMs(timeToStopMs)

  if (!timeFormatted) return undefined

  // Include next stop label if available
  let text = timeFormatted
  if (metrics.nextStopLabel) {
    text = `${metrics.nextStopLabel}: ${timeFormatted}`
  }

  return {
    IconComponent: "Clock",
    colorScheme: "$info",
    id: "pivotTimeToStopIndicator",
    text,
  }
}

export type SensorStatusBadges = {
  [key in
    | SetDifference<SensorName, "battery" | "vfd">
    | "batteryStateOfChargeInternal"
    | "batteryVoltageExternal"
    | "gpsHeading"
    | "pivotRun"
    | "pivotDirection"
    | "pivotWet"
    | "pivotEndGun"
    | "pivotHeading"
    | "pivotSpeed"
    | "pivotTimeToStop"
    | "runEta"]?: StatusBadgeProps
} & {
  vfdStatus?: VfdIndicatorProps | null
}

/**
 * We synthesize different properties of the device in order to display
 * important information to users about it. This interface is the result
 * of that post-api processing and is used in the StatusMap, the DeviceRoster,
 * the SelectedDevice interface and DeviceProfile. Consistency is key for
 * good UX across these platforms!
 */
export interface DeviceStatusData extends DeviceSummary {
  badges: SensorStatusBadges
  batteryState: SensorState<"battery"> | null | undefined
  commentActive: Models.DeviceComment | undefined
  deviceEventTimestamp: string | undefined
  deviceStatus: "offline" | "online"
  gpsHeading: number | null | undefined

  /**
   * TODO: Double-check this
   */
  hasDeviceActivity: boolean
  inField: Models.FarmField | null
  isInMotion: boolean
  isStoppedShort: boolean
  isUpdating: boolean
  nextScheduledAction: Models.ScheduledDeviceAction | undefined
  pressureState: SensorState<"pressure" | "pressureSwitch"> | undefined
  reelRunStatus: ReelRunStatus | null | undefined
  /**
   * Run completion will be calculated if the device event last
   * has a reel sensor with a valid distance current and distance max,
   * with 'valid' meaning an integer; not null or NaN.
   */
  runCompletion: RunCompletion | undefined
  showAdminInfo: boolean
  unreachableUntil: string | null | undefined
}

/**
 *
 */
function makeDeviceStatusListItemCreator({
  deviceActivities,
  lastKnownByDeviceId,
  sensorEventLastsByDeviceId,
  farmLocation,
  fields,
  isAdminModeEnabled,
  measurementPreference,
}: {
  deviceActivities: Dictionary<Models.DeviceActivity>
  lastKnownByDeviceId: Record<string, Models.DeviceLastKnownLocation>
  sensorEventLastsByDeviceId: Record<string, Models.SensorEventLastEntry[]>
  farmLocation: Geo.AnyPoint | undefined
  fields: Models.FarmField[]
  isAdminModeEnabled: boolean
  measurementPreference: MeasurementPreference
}) {
  const validateLocation = makeLocationValidator({ farmLocation })
  return function generateListItem({
    commandTracker,
    comment,
    configuration,
    event,
    flowVolumeL24H
  }: {
    commandTracker: Models.DeviceCommandTracker | undefined
    comment: Models.DeviceComment | undefined
    configuration: DeviceConfiguration
    event: Models.DeviceEvent | undefined
    flowVolumeL24H: number | null | undefined
  }): DeviceStatusData {
    const deviceInstallationType = configuration.deviceInstallationType
    const {
      runDistanceMmCurrent,
      runDistanceMmMax,
      runSpeedMmpm,
      stateCurrent: reelStateCurrent,
    } = event?.reel ?? {}

    const runCompletion = calculateRunCompletion({
      runDistanceMmCurrent,
      runDistanceMmMax,
    })

    const runCompletionPct = runCompletion?.runCompletionPct
    const reelRunStatus = calculateReelRunStatus({
      runCompletionPct,
      stateCurrent: reelStateCurrent,
    })

    const pressureReading = event?.pressure?.readingKpa ?? undefined

    const pressureStateCurrent =
      event?.pressure?.stateCurrent ??
      event?.pressureSwitch?.stateCurrent ??
      undefined

    const wheelStateCurrent = event?.wheel?.stateCurrent
    const flowStateCurrent = event?.flow?.stateCurrent

    const badges: SensorStatusBadges = {
      gps: getGpsBadgeProps({
        isLocationValid: validateLocation(
          event?.gps?.location,
          event?.gps?.stateCurrent,
        ),
      }),
    }

    if (configuration.pressure ?? configuration.pressureSwitch) {
      badges.pressure = getPressureBadgeProps({
        measurementPreference,
        pressureReadingPsi: pressureReading,
        pressureState: pressureStateCurrent,
      })
    }

    if (configuration.flow) {
      // FLOW BADGE

      badges.flow = getFlowBadgeProps({
        flowRateLpmAvg: event?.flow?.rateLpmAvg,
        flowStateCurrent,
        flowVolumeL24H: flowVolumeL24H,
        measurementPreference,
      })
    }

    if (configuration.relay) {
      // RELAY BADGE
      badges.relay = getRelayBadgeProps({
        deviceInstallationType,
        stateCurrent: event?.relay?.stateCurrent,
      })
    }
    if (configuration.hallSwitch) {
      // HALL SWITCH BADGE
      badges.hallSwitch = getHallSwitchProps({
        deviceInstallationType,
        stateCurrent: event?.hallSwitch?.stateCurrent,
      })
    }

    // Check if this is a center pivot installation type (used for badge and activity logic)
    const isCenterPivotType = (CENTER_PIVOT_INSTALLATION_TYPES as readonly string[]).includes(
      deviceInstallationType
    )

    if (event) {
      // VFD
      if (event.vfd) {
        badges.vfdStatus = {
          currentPercentage: event.vfd.currentPercentage,
          pidMeasurementType: event.vfd.pidMeasurementType,
          pidSensorSetpoint: event.vfd.pidSensorSetpoint,
          stateCurrent: event.vfd.stateCurrent,
          targetRampPercentage: event.vfd.targetRampPercentage,
        }
      }
      // GPS
      if (event.gps) {
        // badges.gpsHeading = getGpsHeadingProps({
        //   configuration,
        //   heading: event.gps.heading,
        //   sensorState: reelStateCurrent ?? wheelStateCurrent,
        // })
      }
      // BATTERY BADGES
      if (event.battery) {
        const { internalSoc, stateCurrent, voltageMv } = event.battery

        badges.batteryVoltageExternal = getVoltageBadgeProps({
          internalSoc,
          measurementPreference,
          stateCurrent,
          voltageMv,
        })

        if (typeof internalSoc === "number" && internalSoc <= 50) {
          badges.batteryStateOfChargeInternal = {
            IconComponent: "Battery",
            colorScheme: "$warning",
            id: "internalSoc",
            text: `${internalSoc}%`,
          }
        }
      }
      // WHEEL - skip for center pivot monitors (they show pivot speed instead)
      if (configuration.wheel && !isCenterPivotType) {
        badges.wheel = getWheelBadgeProps({
          event: event.wheel,
          measurementPreference,
        })
      }
      // REEL INDICATORS
      if (configuration.reel && event.reel) {
        if (reelRunStatus) {
          badges.reel = getRunSpeedBadgeProps({
            measurementPreference,
            runSpeedMmpm,
            runStatus: reelRunStatus,
          })
        }
        badges.runEta = getRunEtaIndicatorProps({
          runDistanceMmCurrent,
          runSpeedMmpm,
          runStatus: reelRunStatus,
        })
      }

      // CENTER PIVOT BADGES (controls, heading, speed, and time to stop)
      if (isCenterPivotType) {
        const activity = deviceActivities[configuration.deviceId]
        const lastKnown = lastKnownByDeviceId[configuration.deviceId]

        // Control indicators (Run / Direction / Wet / End Gun) — derived from
        // the CP sensor event last's integer fields. Only the controller has a
        // CP sensor, so these render only on the controller row.
        const cpEntry = (
          sensorEventLastsByDeviceId[configuration.deviceId] ?? []
        ).find((e) => e.sensorType === "CP")
        const controlBadges = getPivotControlBadges(cpEntry)
        badges.pivotRun = controlBadges.pivotRun
        badges.pivotDirection = controlBadges.pivotDirection
        badges.pivotWet = controlBadges.pivotWet
        badges.pivotEndGun = controlBadges.pivotEndGun

        // Pivot heading badge (prefers activity, falls back to last-known GPS fit)
        badges.pivotHeading = getPivotHeadingBadgeProps({
          activity,
          lastKnownPivotHeadingDegrees: lastKnown?.pivotHeadingDegrees ?? null,
        })

        // Pivot speed badge (hours per revolution)
        badges.pivotSpeed = getPivotSpeedBadgeProps({ activity })

        // Pivot time to stop badge
        badges.pivotTimeToStop = getPivotTimeToStopBadgeProps({ activity })
      }

      if (event.temperature) {
        // temperature BADGE
        badges.temperature = getTemperatureBadgeProps({
          measurementPreference,
          stateCurrent: event?.temperature?.stateCurrent,
          readingCelsius: event?.temperature?.readingCelsius,
        })
      }

      if (event.humidity) {
        // humidity BADGE
        badges.humidity = getHumidityBadgeProps({
          measurementPreference,
          stateCurrent: event?.humidity?.stateCurrent,
          relativePercentage: event?.humidity?.relativePercentage,
        })
      }

      if (configuration.depth) {
        badges.depth = getDepthBadgeProps({
          measurementPreference,
          stateCurrent: event?.depth?.stateCurrent,
          measuredM: event?.depth?.measuredM,
        })
      }
    }

    /**
     * Check if device has active irrigation activity
     */
    let hasDeviceActivity = false
    if (Boolean(deviceActivities[configuration.deviceId])) {
      if (isCenterPivotType) {
        hasDeviceActivity = true
      }
    }

    return {
      commentActive:
        comment && comment.status === "active" ? comment : undefined,
      gpsHeading: event?.gps?.heading,
      hasDeviceActivity,
      isInMotion: isDeviceInMotion(reelStateCurrent ?? wheelStateCurrent),
      nextScheduledAction: event?.scheduledDeviceActions[0],
      pressureState: pressureStateCurrent,
      showAdminInfo: isAdminModeEnabled,
      ...makeDeviceSummary({
        configuration,
        event,
        farmLocation,
      }),
      badges,
      batteryState: event?.battery?.stateCurrent,
      deviceEventTimestamp: event?.deviceEventTimestamp,
      deviceStatus: Boolean(event) ? "online" : "offline",
      inField: findFieldForDevice({ event, fields }),
      isStoppedShort: reelRunStatus === "stopped short",
      isUpdating: Boolean(commandTracker),
      reelRunStatus,
      runCompletion,
      unreachableUntil: configuration.unreachableUntil,
    }
  }
}

export const getFlowVolumeL24HInputAllDevices = (state: RootState) => {
  return state.deviceEventLast.flowVolumeL24H
}

export const getFlowVolumeL24HInputByDeviceId = (state: RootState, deviceId: string) => {
  return state.deviceEventLast.flowVolumeL24H?.[deviceId]
}
/**
 * This selector produces a DeviceStatusData object for each
 * device configuration in the farm's account, regardless
 * of whether it has an event in deviceEventLast
 */
export const getDeviceStatusListItemsFromState = createSelector(
  [
    Models.deviceConfiguration.selectEntities,
    Models.deviceEventLast.selectEntities,
    Models.deviceCommandTracker.selectEntities,
    Models.deviceComment.selectEntities,
    getActiveFarmCoordinatesFromState,
    getFlowVolumeL24HInputAllDevices,
    getIsAdminModeEnabledFromState,
    getUserMeasurementPreferenceFromState,
    Models.farmField.selectAll,
    Models.deviceActivity.selectEntities,
    (state: RootState) => state.deviceLastKnownLocations.byDeviceId,
    (state: RootState) => state.deviceEventLast.sensorEventLasts,
  ],
  (
    configurations,
    events,
    commandTrackers,
    comments,
    farmLocation,
    flowVolume24HTotal,
    isAdminModeEnabled,
    measurementPreference,
    fields,
    deviceActivities,
    lastKnownByDeviceId,
    sensorEventLastsByDeviceId,
  ) => {
    const generateListItem = makeDeviceStatusListItemCreator({
      deviceActivities,
      lastKnownByDeviceId,
      sensorEventLastsByDeviceId,
      farmLocation,
      fields,
      isAdminModeEnabled,
      measurementPreference,
    })
    const result: DeviceStatusData[] = []
    for (const configuration of Object.values(configurations)) {
      const deviceId = configuration.deviceId
      if (isTruthyString(deviceId)) {
        result.push(
          generateListItem({
            commandTracker: commandTrackers[deviceId],
            comment: comments[deviceId],
            configuration,
            event: events[deviceId],
            flowVolumeL24H: flowVolume24HTotal?.[deviceId]
          }),
        )
      }
    }
    result.sort(sortDeviceStatusByUrgency)
    return result
  },
)
/**
 * Synthesizes device events along with their configuration, comments, triggers,
 * and other relevant data into a single object that can be used to render
 * a device status display
 */
export const getDeviceStatusDataByDeviceIdFromState: (
  state: RootState,
  deviceId: string,
) => DeviceStatusData | undefined = createCachedSelector(
  [
    Models.deviceConfiguration.selectById,
    Models.deviceEventLast.selectEntities,
    Models.deviceCommandTracker.selectById,
    Models.deviceComment.selectById,
    Models.farmField.selectAll,
    getActiveFarmCoordinatesFromState,
    getFlowVolumeL24HInputByDeviceId,
    getIsAdminModeEnabledFromState,
    getUserMeasurementPreferenceFromState,
    Models.deviceActivity.selectEntities,
    (state: RootState) => state.deviceLastKnownLocations.byDeviceId,
    (state: RootState) => state.deviceEventLast.sensorEventLasts,
  ],
  (
    configuration,
    events,
    commandTracker,
    comment,
    fields,
    farmLocation,
    flowVolume24HTotal,
    isAdminModeEnabled,
    measurementPreference,
    deviceActivities,
    lastKnownByDeviceId,
    sensorEventLastsByDeviceId,
  ) => {
    const generateListItem = makeDeviceStatusListItemCreator({
      deviceActivities,
      lastKnownByDeviceId,
      sensorEventLastsByDeviceId,
      farmLocation,
      fields,
      isAdminModeEnabled,
      measurementPreference,
    })

    if (configuration) {
      const deviceId = configuration.deviceId
      return generateListItem({
        commandTracker,
        comment,
        configuration,
        event: events[deviceId],
        flowVolumeL24H: flowVolume24HTotal
      })
    }
    return undefined
  },
)(makeDeviceEventCacheKey)

/**
 * Get all device status data sorted by the current sort key and direction
 */
export const getDeviceStatusListItemsSortedFromState = createSelector(
  [
    getDeviceStatusListItemsFromState,
    getDeviceSortKeyFromState,
    getDeviceSortDirectionFromState,
  ],
  (devices, sortKey, sortDirection) => {
    const devicesCopied = [...devices]
    let sortCallback: SortComparator<DeviceStatusData>
    switch (sortKey) {
      case "alphabetical": {
        sortCallback = makePrioritySort(sortDirection, sortByDeviceName)
        break
      }
      case "device_activity": {
        sortCallback = makePrioritySort(
          sortDirection,
          sortDeviceStatusByUrgencyAscending,
        )
        break
      }
      case "device_installation_type": {
        sortCallback = makePrioritySort(
          sortDirection,
          sortByDeviceInstallationType,
          sortByDeviceName,
        )
        break
      }
    }
    devicesCopied.sort(sortCallback)
    return devicesCopied
  },
)

const MARKER_VERSION = 5
/**
 * Get the marker image source for a device based on its installation type
 */
export const getMarkerImageSourceByDeviceIdFromState: (
  state: RootState,
  deviceId: string,
) => string = createCachedSelector(
  Models.deviceEventLast.selectById,
  (state, deviceId: string) =>
    Models.deviceConfiguration.selectById(state, deviceId)
      ?.deviceInstallationType,
  (event, installationType) => {
    const pressureStateCurrent =
      event?.pressure?.stateCurrent ?? event?.pressureSwitch?.stateCurrent
    const flowStateCurrent = event?.flow?.stateCurrent

    let isMoving: boolean | undefined

    if (
      installationType === "reel" ||
      installationType === "reel_with_booster_off_only" ||
      installationType === "center_pivot" ||
      installationType === "traveller_soft" ||
      installationType === "linear_move" ||
      installationType === "linear_move_controller" ||
      installationType === "linear_move_monitor"
    ) {
      isMoving = isDeviceInMotion(
        event?.reel?.stateCurrent ?? event?.wheel?.stateCurrent,
      )
    }

    let uri = `${IMAGE_HOST_URL}/generated/v_${MARKER_VERSION}/`
    switch (installationType) {
      case undefined: {
        throw new Error("Not implemented yet: undefined case")
      }
      case "center_pivot": {
        uri += "ag_pivot"
        break
      }
      case "linear_move":
      case "linear_move_controller":
      case "linear_move_monitor": {
        uri += "ag_linear"
        break
      }
      // There is no traveller-specific marker in assets/generated — the
      // available device segments are ag_linear, ag_pivot, disconnected,
      // meter, prototype, pump_*, reel, unconfigured, valve and warning.
      // Matched explicitly rather than sharing the linear marker so the
      // gap is visible; falls back to the prototype marker until a
      // traveller asset is generated.
      case "traveller_soft": {
        uri += "prototype"
        break
      }
      case "pump":
      case "pump_off_only": {
        uri += "pump_off"
        break
      }
      case "pump_on_off": {
        uri += "pump_io"
        break
      }
      case "pump_vfd": {
        uri += "pump_var"
        break
      }
      case "reel_with_booster_off_only":
      case "reel": {
        // If reel is stopped short, we should show a warning icon
        const runCompletion = calculateRunCompletion({
          runDistanceMmCurrent: event?.reel?.runDistanceMmCurrent,
          runDistanceMmMax: event?.reel?.runDistanceMmMax,
        })?.runCompletionPct
        if (
          calculateReelRunStatus({
            runCompletionPct: runCompletion,
            stateCurrent: event?.reel?.stateCurrent,
          }) === "stopped short"
        ) {
          //
          uri += "warning"
        } else {
          uri += "reel"
        }
        break
      }
      case "valve": {
        uri += "valve"
        break
      }

      case "prototype": {
        uri += "prototype"
        break
      }
      case "unconfigured": {
        uri += "unconfigured"
        break
      }
    }
    // Pressure status
    if (pressureStateCurrent === "PHI" || flowStateCurrent === "FLF" || flowStateCurrent === "FLOV") {
      uri += "_pressure-high"
    } else if (pressureStateCurrent === "PLO") {
      uri += "_pressure-low"
    } else if (pressureStateCurrent === "POV") {
      uri += "_pressure-over"
    } else {
      uri += "_pressure-null"
    }
    // Movement status
    if (isMoving === true) {
      uri += "_movement-true"
    } else {
      uri += "_movement-null"
    }
    uri += ".png"

    return uri
  },
)((state, deviceId): string => {
  const eventId = Models.deviceEventLast.selectById(state, deviceId)?.id
  return `${eventId ?? "None"}`
})
